import {Injectable} from "@angular/core";
import {Router} from "@angular/router";
import {AngularPlugin} from "@microsoft/applicationinsights-angularplugin-js";
import {ApplicationInsights} from "@microsoft/applicationinsights-web";
import {environment} from "src/environments/environment";
import {MonitorInfoDto, PerFormaAPIService} from "./pfapi.service";
import {lastValueFrom} from "rxjs";

@Injectable({
    providedIn: "root"
})
export class Monitor {
    private angularPlugin = new AngularPlugin();
    private appInsights: ApplicationInsights;
    private monitorInfo: MonitorInfoDto | null = null;

    constructor(
        private readonly api: PerFormaAPIService,
        private router: Router
    ) {
        this.appInsights = new ApplicationInsights({
            config: {
                connectionString: this.getConnectionString(),
                disableTelemetry: !environment.production, // won't post telemetry unless in Production mode
                enableAutoRouteTracking: true,
                enableAjaxErrorStatusText: true,
                enableCorsCorrelation: true
                // leave sampling at 100% (the default) for now
                //samplingPercentage: !environment.production ? 100 : 10
            },
            extensions: [this.angularPlugin],
            extensionConfig: {
                [this.angularPlugin.identifier]: {router: this.router}
            }
        } as any);

        this.appInsights.loadAppInsights();
    }

    trackEvent(name: string): void {
        this.appInsights.trackEvent({name});
    }

    trackTrace(message: string): void {
        this.appInsights.trackTrace({message});
    }

    // this will send any unhandled exceptions to AppInsights (i.e. not caught by the code already)
    trackException(error: any, properties?: Record<string, any>): void {
        if (!error) return;

        this.appInsights.trackException(error, {
            exception: error,
            severityLevel: 3,
            properties: {
                ...properties,
                url: window.location.href,
                userAgent: navigator.userAgent,
                componentStack: error?.stack || error?.message
            }
        });

        console.log("caught unhandled exception and forwarded to AppInsights", error);
    }

    // retrieves the App Insights connection string
    private getConnectionString = async () => {
        if (this.monitorInfo == null) {
            try {
                this.monitorInfo = await lastValueFrom(this.api.getApiMonitorInfo());
            } catch (error: any) {
                console.log("unable to get monitor info", error);
            }
        }

        return this.monitorInfo?.ConnectionString ?? "";
    };
}
